import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PAYMENTS } from '@/constants/testIds';
import Navigation from '@/components/Navigation';
import {
  Receipt,
  Mail,
  Info,
  CheckCircle2,
  Circle,
  CreditCard,
  Clock,
  AlertTriangle,
  Download,
  FileText,
  Gift,
  Copy,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_TIMELINE = [
  { key: 'intake_submitted', label: 'Initial Enquiry Submitted' },
  { key: 'deposit_paid', label: 'Initial Deposit Paid' },
  { key: 'report_issued', label: 'Initial Report Issued' },
  { key: 'next_stage_confirmed', label: 'Next Stage Confirmed' },
  { key: 'next_stage_booked', label: 'Next Stage Booked' },
  { key: 'next_stage_paid', label: 'Next Stage Paid' },
  { key: 'completed', label: 'Workflow Complete' }
];

function invoiceNumber(bookingId) {
  // ABC-INV-<last 8 of booking id, uppercase>
  return `ABC-INV-${bookingId.replace(/-/g, '').slice(-8).toUpperCase()}`;
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function StageProgress({ currentStage }) {
  const currentIdx = STAGE_TIMELINE.findIndex((s) => s.key === currentStage);
  return (
    <ol className="space-y-2">
      {STAGE_TIMELINE.map((step, idx) => {
        const completed = idx < currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <li
            key={step.key}
            data-testid={PAYMENTS.stageStep}
            className="flex items-center gap-3 text-sm"
          >
            {completed || isCurrent ? (
              <CheckCircle2
                className={`w-4 h-4 flex-shrink-0 ${completed ? 'text-green-600' : 'text-blue-600'}`}
              />
            ) : (
              <Circle className="w-4 h-4 flex-shrink-0 text-slate-300" />
            )}
            <span
              className={
                completed
                  ? 'text-slate-500 line-through'
                  : isCurrent
                    ? 'text-black font-medium'
                    : 'text-slate-500'
              }
            >
              {step.label}
            </span>
            {isCurrent && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-sm">
                Current
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function Payments() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState({});
  const [error, setError] = useState('');
  const [referrals, setReferrals] = useState(null);
  const [copied, setCopied] = useState(false);
  // Billing & receipt UI state
  const [billingOpen, setBillingOpen] = useState({});       // bookingId -> bool
  const [billingDrafts, setBillingDrafts] = useState({});   // bookingId -> billing fields
  const [receiptHtml, setReceiptHtml] = useState({});       // bookingId -> html string
  const [receiptOpenFor, setReceiptOpenFor] = useState(null); // bookingId currently displayed

  const fetchBookings = useCallback(async () => {
    try {
      const [bookingsRes, referralsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/mine`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBookings(bookingsRes.data);
      setReferrals(referralsRes.data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError('Unable to load your invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handlePay = async (bookingId) => {
    setPaying((prev) => ({ ...prev, [bookingId]: true }));
    try {
      // 1. Persist any billing details the user has filled in (no-op if untouched)
      const draft = billingDrafts[bookingId];
      if (draft && Object.values(draft).some((v) => (v || '').trim().length > 0)) {
        await axios.put(
          `${API}/bookings/${bookingId}/billing`,
          {
            billing_name: draft.billing_name || null,
            billing_email: draft.billing_email || null,
            billing_company: draft.billing_company || null,
            billing_project: draft.billing_project || null,
            billing_abn: draft.billing_abn || null,
            billing_address: draft.billing_address || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // 2. Create Stripe checkout session and redirect
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/checkout?booking_id=${bookingId}&origin_url=${encodeURIComponent(originUrl)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Payment failed:', err);
      setError(err.response?.data?.detail || 'Could not start the payment session. Please try again.');
      setPaying((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleViewReceipt = async (bookingId) => {
    try {
      if (!receiptHtml[bookingId]) {
        const r = await axios.get(`${API}/bookings/${bookingId}/receipt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReceiptHtml((prev) => ({ ...prev, [bookingId]: r.data.html }));
      }
      setReceiptOpenFor(bookingId);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to load the receipt right now.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </div>
    );
  }

  const totalDue = bookings
    .filter((b) => b.payment_status !== 'paid')
    .reduce((sum, b) => sum + (b.budget || 0), 0);
  const totalPaid = bookings
    .filter((b) => b.payment_status === 'paid')
    .reduce((sum, b) => sum + (b.budget || 0), 0);

  return (
    <div data-testid={PAYMENTS.page} className="min-h-screen bg-slate-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-6 py-8 mt-16">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold tracking-tight text-black mb-2"
            style={{ fontFamily: 'Outfit' }}
          >
            Payments &amp; Invoices
          </h1>
          <p className="text-slate-600">
            View your tax invoices, payment status, and pay any approved amounts to All Build Consult.
          </p>
        </div>

        {/* Tax Invoice notice */}
        <div
          data-testid={PAYMENTS.notice}
          className="grid-border bg-blue-50 border-blue-200 p-5 mb-6 flex items-start gap-3"
        >
          <Mail className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-800">
            <p className="font-semibold text-blue-900 mb-1">Tax Invoice &amp; Payment Detail</p>
            <p>
              All Build Consult will email you a <strong>Tax Invoice</strong> and full payment details
              upon receipt of your <strong>Initial Enquiry</strong>, pending approval of your request.
              Once approved, the corresponding invoice below will become payable and you can settle it
              securely from this page.
            </p>
          </div>
        </div>

        {/* Referral & Discount panel */}
        {referrals && (
          <div
            data-testid="payments-referral-panel"
            className="grid-border bg-gradient-to-br from-amber-50 to-rose-50 border-amber-200 p-6 mb-6"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-amber-800" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-black" style={{ fontFamily: 'Outfit' }}>
                    Refer &amp; Save on Your Next Report
                  </h3>
                  <p className="text-sm text-slate-700 max-w-2xl">
                    Share your referral code below. Each new customer who signs up and makes a
                    successful purchase unlocks a discount on <strong>your next report</strong>.
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  Your current discount
                </div>
                <div
                  data-testid="payments-discount-percent"
                  className="text-4xl font-bold text-amber-900"
                  style={{ fontFamily: 'Outfit' }}
                >
                  {referrals.discount_percent}%
                </div>
                <div className="text-xs text-slate-600">
                  {referrals.discount_percent === 30
                    ? 'Max tier unlocked'
                    : `Next tier: ${referrals.next_tier_at}`}
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div className="bg-white grid-border p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Your referral code
                </div>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="payments-referral-code"
                    className="text-2xl font-bold tracking-widest text-black"
                  >
                    {referrals.referral_code}
                  </code>
                  <button
                    data-testid="payments-referral-copy"
                    onClick={() => {
                      navigator.clipboard.writeText(referrals.referral_code);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-xs"
                  >
                    <Copy className="w-3 h-3" />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  New users enter this code on the registration form.
                </p>
              </div>
              <div className="bg-white grid-border p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                  Activity
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>People referred</span>
                  <span className="font-bold">{referrals.referred_users_total}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span>Successful purchases</span>
                  <span className="font-bold">{referrals.successful_referrals}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-1 text-[11px] text-center">
                  {referrals.tier_breakdown.map((t) => (
                    <div
                      key={t.successful_purchases}
                      className={`p-2 rounded-sm ${
                        referrals.discount_percent === t.discount_percent
                          ? 'bg-amber-100 text-amber-900 font-semibold'
                          : 'bg-slate-50 text-slate-600'
                      }`}
                    >
                      <Sparkles className="w-3 h-3 inline mr-0.5" />
                      {t.successful_purchases} → {t.discount_percent}%
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="grid-border bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Outstanding Balance
            </div>
            <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Outfit' }}>
              A${totalDue.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Across all unpaid invoices</div>
          </div>
          <div className="grid-border bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Total Paid
            </div>
            <div className="text-3xl font-bold text-green-700" style={{ fontFamily: 'Outfit' }}>
              A${totalPaid.toFixed(2)}
            </div>
            <div className="text-xs text-slate-500 mt-1">Cumulative settled with All Build Consult</div>
          </div>
          <div className="grid-border bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Invoices
            </div>
            <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Outfit' }}>
              {bookings.length}
            </div>
            <div className="text-xs text-slate-500 mt-1">Linked to your enquiries &amp; bookings</div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {bookings.length === 0 ? (
          <div
            data-testid={PAYMENTS.emptyState}
            className="grid-border bg-white p-12 text-center"
          >
            <Receipt className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>
              No invoices yet
            </h3>
            <p className="text-slate-600 mb-6">
              Submit a pre-assessment to receive your first Tax Invoice from All Build Consult.
            </p>
            <button
              onClick={() => navigate('/bookings')}
              className="px-6 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all font-medium"
            >
              Start Pre-Assessment
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {bookings.map((b) => {
              const isPaid = b.payment_status === 'paid';
              const isProcessing = paying[b.id];
              return (
                <div
                  key={b.id}
                  data-testid={PAYMENTS.invoiceCard}
                  className="grid-border bg-white p-6"
                >
                  <div className="grid lg:grid-cols-3 gap-6">
                    {/* Invoice header */}
                    <div className="lg:col-span-2">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                            Tax Invoice
                          </div>
                          <h3
                            className="text-xl font-semibold text-black"
                            style={{ fontFamily: 'Outfit' }}
                          >
                            {invoiceNumber(b.id)}
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">{b.service_type}</p>
                        </div>
                        <span
                          className={`px-3 py-1 text-xs rounded-sm font-medium ${
                            isPaid
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-900'
                          }`}
                        >
                          {isPaid ? 'Paid' : 'Awaiting Payment'}
                        </span>
                      </div>

                      <div className="text-sm text-slate-600 space-y-1 mb-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>Issued {formatDate(b.created_at)}</span>
                        </div>
                        {b.property_address && (
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 mt-0.5" />
                            <span>{b.property_address}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-slate-50 grid-border p-4 mb-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                          Stage Progress
                        </div>
                        <StageProgress currentStage={b.stage || 'intake_submitted'} />
                      </div>

                      {b.report_pdf_path && (() => {
                        const isPaidForDl = b.payment_status === 'paid';
                        const isLocked = b.download_locked;
                        const expiresIso = b.download_expires_at;
                        let expired = false;
                        let daysLeft = null;
                        if (expiresIso) {
                          const exp = new Date(expiresIso).getTime();
                          const diffMs = exp - Date.now();
                          expired = diffMs <= 0;
                          if (!expired) {
                            daysLeft = Math.max(1, Math.ceil(diffMs / 86400000));
                          }
                        }
                        const blocked = !isPaidForDl || isLocked || expired;
                        return (
                          <div data-testid="payments-download-area" className="space-y-2">
                            {!isPaidForDl && (
                              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 rounded-sm">
                                Payment required to download this report.
                              </div>
                            )}
                            {isLocked && (
                              <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                                Download is currently locked by All Build Consult. Please contact admin.
                              </div>
                            )}
                            {expired && (
                              <div className="text-xs text-rose-800 bg-rose-50 border border-rose-200 px-3 py-2 rounded-sm">
                                Download window for this report has expired. Please contact admin.
                              </div>
                            )}
                            {!blocked && daysLeft !== null && (
                              <div className="text-xs text-slate-600">
                                Download window: <strong className="text-black">{daysLeft} day{daysLeft === 1 ? '' : 's'} left</strong>
                              </div>
                            )}
                            {blocked ? (
                              <button
                                disabled
                                data-testid="payments-download-blocked"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm text-sm font-medium text-slate-400 cursor-not-allowed"
                              >
                                <Download className="w-4 h-4" />
                                Report Locked
                              </button>
                            ) : (
                              <a
                                data-testid="payments-download-link"
                                href={`${API}/bookings/${b.id}/report-pdf?auth=${token}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-sm font-medium"
                              >
                                <Download className="w-4 h-4" />
                                Download Report (PDF)
                              </a>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Payment column */}
                    <div className="lg:border-l lg:border-slate-200 lg:pl-6">
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                        Amount {isPaid ? 'Paid' : 'Payable'}
                      </div>
                      <div
                        className="text-4xl font-bold text-black mb-2"
                        style={{ fontFamily: 'Outfit' }}
                      >
                        A${(b.budget || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-slate-500 mb-5">Currency: AUD · GST inclusive</div>

                      {isPaid ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-800">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="font-medium">Payment received</span>
                          </div>
                          <button
                            data-testid="payments-view-receipt"
                            onClick={() => handleViewReceipt(b.id)}
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-sm font-medium inline-flex items-center justify-center gap-2"
                          >
                            <FileText className="w-4 h-4" />
                            View / Email Receipt
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <button
                            data-testid="payments-billing-toggle"
                            onClick={() =>
                              setBillingOpen((prev) => ({ ...prev, [b.id]: !prev[b.id] }))
                            }
                            className="w-full px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-50 transition-all text-xs font-medium inline-flex items-center justify-center gap-2"
                          >
                            <Info className="w-3.5 h-3.5" />
                            {billingOpen[b.id] ? 'Hide billing details' : 'Add / edit billing details'}
                          </button>

                          {billingOpen[b.id] && (() => {
                            const draft = billingDrafts[b.id] || {
                              billing_name: b.billing_name || '',
                              billing_email: b.billing_email || '',
                              billing_company: b.billing_company || '',
                              billing_project: b.billing_project || '',
                              billing_abn: b.billing_abn || '',
                              billing_address: b.billing_address || b.property_address || ''
                            };
                            const setField = (field, val) =>
                              setBillingDrafts((prev) => ({
                                ...prev,
                                [b.id]: { ...draft, [field]: val }
                              }));
                            const inputCls =
                              'w-full px-2 py-1.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:border-black';
                            return (
                              <div
                                data-testid="payments-billing-form"
                                className="grid gap-2 p-3 bg-slate-50 border border-slate-200 rounded-sm"
                              >
                                <input
                                  data-testid="billing-name"
                                  placeholder="Full name"
                                  value={draft.billing_name}
                                  onChange={(e) => setField('billing_name', e.target.value)}
                                  className={inputCls}
                                />
                                <input
                                  data-testid="billing-email"
                                  type="email"
                                  placeholder="Email for receipt"
                                  value={draft.billing_email}
                                  onChange={(e) => setField('billing_email', e.target.value)}
                                  className={inputCls}
                                />
                                <input
                                  data-testid="billing-company"
                                  placeholder="Company (optional)"
                                  value={draft.billing_company}
                                  onChange={(e) => setField('billing_company', e.target.value)}
                                  className={inputCls}
                                />
                                <input
                                  data-testid="billing-project"
                                  placeholder="Project / cost-centre (optional)"
                                  value={draft.billing_project}
                                  onChange={(e) => setField('billing_project', e.target.value)}
                                  className={inputCls}
                                />
                                <input
                                  data-testid="billing-abn"
                                  placeholder="ABN (optional)"
                                  value={draft.billing_abn}
                                  onChange={(e) => setField('billing_abn', e.target.value)}
                                  className={inputCls}
                                />
                                <input
                                  data-testid="billing-address"
                                  placeholder="Billing address (optional)"
                                  value={draft.billing_address}
                                  onChange={(e) => setField('billing_address', e.target.value)}
                                  className={inputCls}
                                />
                                <p className="text-[10px] text-slate-500">
                                  Saved on the booking before checkout; appears on your Tax
                                  Invoice and receipt.
                                </p>
                              </div>
                            );
                          })()}

                          <button
                            data-testid={PAYMENTS.payButton}
                            onClick={() => handlePay(b.id)}
                            disabled={isProcessing}
                            className="w-full px-4 py-3 bg-black text-white rounded-sm hover:bg-slate-800 transition-all text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <CreditCard className="w-4 h-4" />
                            {isProcessing ? 'Redirecting to checkout…' : 'Pay this Invoice'}
                          </button>
                        </div>
                      )}

                      <div className="mt-4 flex items-start gap-2 text-[11px] text-slate-500">
                        <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>
                          The Tax Invoice with full breakdown is emailed by All Build Consult on
                          approval. Payment is processed securely via Stripe.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Receipt modal */}
      {receiptOpenFor && receiptHtml[receiptOpenFor] && (
        <div
          data-testid="payments-receipt-modal"
          onClick={() => setReceiptOpenFor(null)}
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-6 overflow-y-auto"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-xl max-w-2xl w-full my-10"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div className="text-sm font-semibold text-black">Payment Receipt</div>
              <button
                data-testid="payments-receipt-close"
                onClick={() => setReceiptOpenFor(null)}
                className="text-slate-500 hover:text-black text-sm"
              >
                Close ✕
              </button>
            </div>
            <div
              className="p-4"
              dangerouslySetInnerHTML={{ __html: receiptHtml[receiptOpenFor] }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
